import React, { useState } from 'react';
import { useI18n } from '../../../../i18n';
import type { ReindexLogEntry } from '../../../shared/services/api';
import type { ModelOption, Proveedor } from './admin-ia-shared';
import {
  INPUT_STYLE,
  LABEL_STYLE,
  SECTION_CARD,
  SECTION_TITLE,
  PROVIDER_NAMES,
} from './admin-ia-shared';
import { InfoTooltip } from '@/components/ui/InfoTooltip';

interface AdminIAChatConfigProps {
  chatModel: string;
  chatLimit: number;
  chatProviderOptions: ModelOption[];
  fallbackChain: string[];
  providers: Proveedor[];
  configData: Record<string, any> | null;
  ragStatus: {
    totalChunks: number;
    docsIndexados: number;
    ultimaIndexacion: string;
  } | null;
  reindexing: boolean;
  reindexForce: boolean;
  onReindexForceChange: (value: boolean) => void;
  reindexIntervalMs: number;
  onReindexIntervalChange: (ms: number) => void;
  reindexHistory: ReindexLogEntry[];
  onChatModelChange: (value: string) => void;
  onChatLimitChange: (value: number) => void;
  onRemoveFallback: (idx: number) => void;
  onAddFallback: (tipo: string) => void;
  onReorderFallback: (from: number, to: number) => void;
  onReindex: () => void;
}

export function AdminIAChatConfig({
  chatModel,
  chatLimit,
  chatProviderOptions,
  fallbackChain,
  providers,
  configData,
  ragStatus,
  reindexing,
  reindexForce,
  onReindexForceChange,
  reindexIntervalMs,
  onReindexIntervalChange,
  reindexHistory,
  onChatModelChange,
  onChatLimitChange,
  onRemoveFallback,
  onAddFallback,
  onReorderFallback,
  onReindex,
}: AdminIAChatConfigProps) {
  const [draggingIdx, setDraggingIdx] = useState<number | null>(null);
  const [dropTargetIdx, setDropTargetIdx] = useState<number | null>(null);
  const [focusedIdx, setFocusedIdx] = useState<number | null>(null);
  const { t } = useI18n();
  return (
    <div style={SECTION_CARD}>
      <h3 style={{ ...SECTION_TITLE, display: 'inline-flex', alignItems: 'center' }}>
        {t.admin.ia.chat.titulo}
        <InfoTooltip title={t.admin.ia.chat.titulo} text={t.admin.ia.chat.ayuda} />
      </h3>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Model selector */}
        <div>
          <label style={LABEL_STYLE}>Modelo principal</label>
          <select
            style={{ ...INPUT_STYLE, cursor: 'pointer' }}
            value={chatModel}
            onChange={(e) => onChatModelChange(e.target.value)}
          >
            {chatProviderOptions.length > 0 ? (
              chatProviderOptions.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))
            ) : (
              <option value={chatModel}>{chatModel || 'Sin modelos disponibles'}</option>
            )}
          </select>
        </div>

        {/* Message limit */}
        <div>
          <label style={LABEL_STYLE}>Limite mensajes/usuario/dia</label>
          <input
            type="number"
            style={{ ...INPUT_STYLE, width: 100 }}
            value={chatLimit}
            onChange={(e) => onChatLimitChange(parseInt(e.target.value) || 50)}
          />
        </div>
      </div>

      {/* Fallback chain */}
      <div style={{ marginTop: 14 }}>
        <label style={LABEL_STYLE}>Cadena de fallback</label>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Principal provider (not removable) */}
          <span
            style={{
              padding: '4px 12px',
              borderRadius: 20,
              background: '#1F3864',
              color: '#fff',
              fontSize: 11,
              fontWeight: 600,
            }}
          >
            {PROVIDER_NAMES[(configData?.proveedorPredeterminado ?? '').toLowerCase()] ??
              configData?.proveedorPredeterminado ??
              'Principal'}
          </span>
          {fallbackChain.length > 0 && <span style={{ color: '#aaa', fontSize: 12 }}>&rarr;</span>}

          {/* Fallbacks (reordenables + removable) */}
          {fallbackChain.map((fb, idx) => {
            const isDropTarget =
              dropTargetIdx === idx && draggingIdx !== null && draggingIdx !== idx;
            const isDragging = draggingIdx === idx;
            return (
              <React.Fragment key={fb}>
                <span
                  draggable
                  tabIndex={0}
                  role="button"
                  aria-label={`${PROVIDER_NAMES[fb.toLowerCase()] ?? fb} — usa ArrowUp/ArrowDown para reordenar, Delete para quitar`}
                  onDragStart={(e) => {
                    setDraggingIdx(idx);
                    e.dataTransfer.effectAllowed = 'move';
                    e.dataTransfer.setData('text/plain', String(idx));
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                    if (dropTargetIdx !== idx) setDropTargetIdx(idx);
                  }}
                  onDragLeave={() => {
                    if (dropTargetIdx === idx) setDropTargetIdx(null);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    const fromIdx = parseInt(e.dataTransfer.getData('text/plain'), 10);
                    if (!Number.isNaN(fromIdx)) onReorderFallback(fromIdx, idx);
                    setDraggingIdx(null);
                    setDropTargetIdx(null);
                  }}
                  onDragEnd={() => {
                    setDraggingIdx(null);
                    setDropTargetIdx(null);
                  }}
                  onFocus={() => setFocusedIdx(idx)}
                  onBlur={() => setFocusedIdx((curr) => (curr === idx ? null : curr))}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowUp' && idx > 0) {
                      e.preventDefault();
                      onReorderFallback(idx, idx - 1);
                    } else if (e.key === 'ArrowDown' && idx < fallbackChain.length - 1) {
                      e.preventDefault();
                      onReorderFallback(idx, idx + 1);
                    } else if (e.key === 'Delete' || e.key === 'Backspace') {
                      e.preventDefault();
                      onRemoveFallback(idx);
                    }
                  }}
                  style={{
                    padding: '4px 10px 4px 8px',
                    borderRadius: 20,
                    background: '#D6E4F0',
                    color: '#1F3864',
                    fontSize: 11,
                    fontWeight: 500,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    cursor: 'grab',
                    opacity: isDragging ? 0.4 : 1,
                    borderLeft: isDropTarget ? '3px solid #2F5496' : '3px solid transparent',
                    userSelect: 'none',
                    outline: focusedIdx === idx ? '2px solid #2F5496' : 'none',
                    outlineOffset: focusedIdx === idx ? 2 : 0,
                  }}
                >
                  <span
                    aria-hidden
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: '#2F5496',
                      opacity: 0.7,
                      letterSpacing: -1,
                    }}
                  >
                    {'\u2807'}
                  </span>
                  {PROVIDER_NAMES[fb.toLowerCase()] ?? fb}
                  <span
                    onClick={() => onRemoveFallback(idx)}
                    onMouseDown={(e) => e.stopPropagation()}
                    style={{ cursor: 'pointer', fontSize: 13, fontWeight: 700, opacity: 0.6 }}
                  >
                    &times;
                  </span>
                </span>
                {idx < fallbackChain.length - 1 && (
                  <span style={{ color: '#aaa', fontSize: 12 }}>&rarr;</span>
                )}
              </React.Fragment>
            );
          })}

          {/* Add button */}
          <select
            onChange={(e) => {
              if (e.target.value) onAddFallback(e.target.value);
              e.target.value = '';
            }}
            style={{
              padding: '4px 8px',
              borderRadius: 20,
              border: '1px dashed #D9D9D9',
              fontSize: 10,
              color: '#595959',
              background: 'transparent',
              cursor: 'pointer',
            }}
          >
            <option value="">+ Anadir</option>
            {providers
              .filter(
                (p) =>
                  p.activo &&
                  !fallbackChain.includes(p.tipo) &&
                  p.tipo !== configData?.proveedorPredeterminado,
              )
              .map((p) => (
                <option key={p.tipo} value={p.tipo}>
                  {p.nombre}
                </option>
              ))}
          </select>
        </div>
      </div>

      {/* RAG status */}
      <div style={{ marginTop: 14 }}>
        <label style={{ ...LABEL_STYLE, display: 'inline-flex', alignItems: 'center' }}>
          {t.admin.ia.rag.titulo}
          <InfoTooltip title={t.admin.ia.rag.titulo} text={t.admin.ia.rag.ayuda} />
        </label>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {/* Contador docs/chunks */}
          <div
            style={{
              background: '#f3f4f6',
              borderRadius: 6,
              padding: '6px 12px',
              fontSize: 12,
              color: '#374151',
              flex: 1,
              minWidth: 160,
            }}
          >
            {ragStatus
              ? `${ragStatus.docsIndexados} docs \u00B7 ${ragStatus.totalChunks} chunks`
              : 'Sin datos de indice RAG'}
          </div>

          {/* Toggle Solo cambios / Todo */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 11,
              color: '#6b7280',
            }}
          >
            <span
              style={{
                color: !reindexForce ? '#1F3864' : '#9ca3af',
                fontWeight: !reindexForce ? 600 : 400,
              }}
            >
              {t.admin.ia.rag.soloCambios}
            </span>
            <div
              role="switch"
              aria-checked={reindexForce}
              aria-label={reindexForce ? t.admin.ia.rag.todo : t.admin.ia.rag.soloCambios}
              tabIndex={0}
              onClick={() => onReindexForceChange(!reindexForce)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onReindexForceChange(!reindexForce);
                }
              }}
              style={{
                width: 32,
                height: 18,
                borderRadius: 9,
                background: reindexForce ? '#2F5496' : '#D1D5DB',
                position: 'relative',
                cursor: 'pointer',
                transition: 'background 200ms',
                outline: 'none',
                flexShrink: 0,
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  top: 2,
                  left: reindexForce ? 16 : 2,
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  background: '#fff',
                  transition: 'left 200ms',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                }}
              />
            </div>
            <span
              style={{
                color: reindexForce ? '#1F3864' : '#9ca3af',
                fontWeight: reindexForce ? 600 : 400,
              }}
            >
              {t.admin.ia.rag.todo}
            </span>
          </div>

          {/* Boton Reindexar */}
          <button
            disabled={reindexing}
            onClick={onReindex}
            style={{
              padding: '6px 14px',
              background: reindexing ? '#6b7280' : '#0E243E',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              fontSize: 11,
              fontWeight: 600,
              cursor: reindexing ? 'default' : 'pointer',
              fontFamily: 'inherit',
              opacity: reindexing ? 0.7 : 1,
            }}
          >
            {reindexing ? t.admin.ia.rag.indexando : t.admin.ia.rag.reindexar}
          </button>
        </div>

        {ragStatus?.ultimaIndexacion && (
          <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 4 }}>
            {t.admin.ia.rag.ultimaIndexacion}:{' '}
            {new Date(ragStatus.ultimaIndexacion).toLocaleString()}
          </div>
        )}

        {/* Intervalo de reindexado automatico */}
        <div style={{ fontSize: 11, color: '#6b7280', marginTop: 6 }}>
          {t.admin.ia.rag.autoCada}{' '}
          <input
            type="number"
            min={5}
            max={1440}
            value={Math.round(reindexIntervalMs / 60000)}
            onChange={(e) => {
              const mins = parseInt(e.target.value, 10);
              if (!Number.isNaN(mins) && mins >= 5 && mins <= 1440) {
                onReindexIntervalChange(mins * 60000);
              }
            }}
            style={{
              width: 52,
              textAlign: 'center',
              fontSize: 11,
              border: '1px solid #E5E7EB',
              borderRadius: 4,
              padding: '1px 4px',
              fontFamily: 'inherit',
            }}
          />{' '}
          {t.admin.ia.rag.minutos}
        </div>

        {/* Timeline de reindexados automaticos */}
        {reindexHistory.length > 0 && (
          <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 4 }}>
            {t.admin.ia.rag.ultimosCiclos}:{' '}
            {reindexHistory
              .slice(0, 3)
              .map((r) => `${new Date(r.ts).toLocaleTimeString()} \u2014 ${r.totalDocs} docs`)
              .join(' \u00B7 ')}
          </div>
        )}
      </div>
    </div>
  );
}
